import { Helmet } from 'react-helmet-async';
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useParams, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import useRefMounted from 'src/hooks/useRefMounted';

import {
  Grid,
} from '@mui/material';

import Scheduler from './Scheduler';
import AppointmentSuccess from './AppointmentSuccess';
import AppointmentError from './AppointmentError';
import SuspenseLoader from 'src/components/SuspenseLoader';

import tenantMappings from 'src/utils/tenantMappings';

// import { getPublicOnlineBookingPageAPI } from 'src/content/OnlineBooking/api';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

const OnlineBookingIndex = () => {
  const isMountedRef = useRefMounted();
  const { onlineBookingId } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [pageData, setPageData] = useState<any>({});
  const [servicesList, setServicesList] = useState<any[]>([]);
  const [error, setError] = useState('');
  const location = useLocation();

  const userMappings = tenantMappings?.userMappings;

  const parsed = Object.fromEntries(
    Object.entries(queryString.parse(location.search)).map(([k, v]) => [
      k.toLowerCase(),
      v
    ])
  );

  const [currentScreen, setCurrentScreen] = useState('schedule');

  const getOnlineBookingPageData = useCallback(async () => {
    const graphQLquery = {
      // operationName: 'getOnlineBookingPage',
      query: `query GetOnlineBookingPage($id: ID!) {\n  getOnlineBookingPage(id: $id) {\n    id\n    isActive\n    tenantId\n    name\n    services {\n      id\n      locationId\n      practitionerId\n      duration\n      onlineBookingCategory\n      onlineBookingName\n      onlineBookingDescription\n    }\n    classServices {\n      id\n      locationId\n      practitionerId\n      duration\n      onlineBookingCategory\n      onlineBookingName\n      onlineBookingDescription\n     }\n    practitioners {\n      id\n      preferredName\n      description\n    jobTitle\n    profilePicture\n}\n    locations {\n      id\n      code\n  addressStreetAddress\n  addressUnitNo\n  addressPostalCode\n  addressCountry\n    }\n    headHTML\n    contentHTML\n    bookingFinePrint\n    allowedDaysInFuture\n    customFields\n    thirdPartyTools\n}\n}\n`,
      variables: { id: onlineBookingId }
    };
    axios
      .post(process.env.REACT_APP_AWS_GRAPHQL_API, graphQLquery, {
        headers: {
          'x-api-key': process.env.REACT_APP_AWS_CONFIRMATION_API,
          accept: 'application/json, text/plain, */*',
          'accept-language': 'en-GB,en;q=0.9,en-US;q=0.8,vi;q=0.7',
          'content-type': 'application/json'
        }
      })
      .then((res) => {
        // console.log(res);
        if (
          res.status === 200 &&
          res.data?.data?.getOnlineBookingPage?.isActive
        ) {
          setPageData(res.data.data.getOnlineBookingPage);

          // If classes are available
          const generatedClassList =
            res.data.data.getOnlineBookingPage?.classServices.map(
              (classService) => ({
                ...classService,
                providerName:
                  res.data.data.getOnlineBookingPage.practitioners.find(
                    (prac) => prac.id === classService.practitionerId
                  ).preferredName,
                locationName: res.data.data.getOnlineBookingPage.locations.find(
                  (loc) => loc.id === classService.locationId
                ).code,
                providerDescription:
                  res.data.data.getOnlineBookingPage.practitioners.find(
                    (prac) => prac.id === classService.practitionerId
                  )?.description,
                practitionerJobTitle:
                  res.data.data.getOnlineBookingPage.practitioners.find(
                    (prac) => prac.id === classService.practitionerId
                  )?.jobTitle,
                profilePicture:
                  res.data.data.getOnlineBookingPage.practitioners.find(
                    (prac) => prac.id === classService.practitionerId
                  )?.profilePicture,

                onlineBookingName: `${classService?.onlineBookingName} `, // Space left at the end intentionally. hacky way to differentiate from Service
                type: 'Class Service'
              })
            ) || [];

          const generatedList = res.data.data.getOnlineBookingPage.services.map(
            (service) => ({
              ...service,
              providerName:
                res.data.data.getOnlineBookingPage.practitioners.find(
                  (prac) => prac.id === service.practitionerId
                ).preferredName,
              locationName: res.data.data.getOnlineBookingPage.locations.find(
                (loc) => loc.id === service.locationId
              ).code,
              providerDescription:
                res.data.data.getOnlineBookingPage.practitioners.find(
                  (prac) => prac.id === service.practitionerId
                )?.description,
              practitionerJobTitle:
                res.data.data.getOnlineBookingPage.practitioners.find(
                  (prac) => prac.id === service.practitionerId
                )?.jobTitle,
              profilePicture:
                res.data.data.getOnlineBookingPage.practitioners.find(
                  (prac) => prac.id === service.practitionerId
                )?.profilePicture,
              type: 'Service'
            })
          );

          const combinedList = [...generatedClassList, ...generatedList];

          setServicesList(combinedList);
          setIsLoading(false);
        } else {
          setIsLoading(false);
          // console.log('something went wrong....')
          setError('Looks like something went wrong.');
        }

        if (
          res.data.data.getOnlineBookingPage?.services.length === 0 &&
          res.data.data.getOnlineBookingPage?.classServices.length === 0
        ) {
          setError('Looks like something went wrong.');
        }
      })
      .catch((err) => {
        console.log(err);
        setIsLoading(false);
        setError('Looks like something went wrong.');
      });
  }, [onlineBookingId]);

  useEffect(() => {
    getOnlineBookingPageData();
  }, []);

  const facebookKey =
    pageData &&
    pageData?.thirdPartyTools &&
    JSON.parse(pageData?.thirdPartyTools[0])['facebookPixel']['accessKey'] !==
      ''
      ? JSON.parse(pageData?.thirdPartyTools[0])['facebookPixel']['accessKey']
      : '';

  useEffect(() => {
    if (facebookKey && facebookKey !== '') {
      // console.log('pixel starting', facebookKey);
      const ReactPixel = require('react-facebook-pixel');
      ReactPixel.default.init(facebookKey);
    }
  }, [facebookKey]);

  // console.log(onlineBookingId);
  // console.log(parsed);
  // console.log(pageData);

  if (isLoading) {
    return <SuspenseLoader />;
  }

  // console.log(servicesList);
  return (
    <>
      {/* <Box> */}
      <GoogleReCaptchaProvider reCaptchaKey="6LdCWMoqAAAAADJVxa0Oc2-1t9fWyOUEoCXooHjV">
        <Helmet>
          <script>$crisp.push(['do', 'chat:hide']);</script>

          <title>Helm - Online Booking</title>

          <meta
            name="description"
            content={`Book your appointment with ${userMappings?.getPublicTenantName(
              pageData?.tenantId
            )} now!`}
            data-react-helmet="true"
          />
        </Helmet>
        <Grid
          container
          spacing={0}
          justifyContent="center"
          justifyItems="center"
          sx={{ backgroundColor: '#F2F2F2', minHeight: '150%' }}
        >
          {/* <Grid item xs={0} sm={2} /> */}
          {error !== '' && (
            <Grid item xs={12}>
              {' '}
              <AppointmentError />{' '}
            </Grid>
          )}

          {error === '' && currentScreen === 'schedule' && (
            <Grid item xs={12} sm={8}>
              <Scheduler
                contentHTML={pageData?.contentHTML}
                setCurrentScreen={setCurrentScreen}
                tenantId={pageData?.tenantId}
                servicesList={servicesList}
                onlineBookingPageId={onlineBookingId}
                urlQueryParams={parsed}
                customFields={pageData?.customFields}
                allowedDaysInFuture={pageData?.allowedDaysInFuture}
                locationDetails={pageData?.locations}
              />
            </Grid>
          )}
          {/* {error === '' && currentScreen === 'success' && <AppointmentSuccess />}
        {error === '' && currentScreen === 'error' && <AppointmentError />} */}

          {/* <Grid item xs={0} sm={2} /> */}
        </Grid>
      </GoogleReCaptchaProvider>
    </>
  );
};

export default OnlineBookingIndex;
